<template>
  <div v-if="event && ticket">
    <div class="w-full flex flex-col mt-2.5">
      <div v-for="(value, key) in eventInfo" :key="key" class="w-full py-0.5 flex justify-between odd:bg-accent">
        <span class="pl-1.5 font-medium">{{ $t(key) }}</span>
        <!-- Payment Details is an array, so show each line -->
        <span v-if="Array.isArray(value)" class="w-2/3 pr-1.5 text-right">
          <div v-for="(line, i) in value" :key="i" class="block text-sm">{{ line }}</div>
        </span>
        <span v-else class="w-2/3 pr-1.5 text-sm text-right break-words">{{ value }}</span>
      </div>
    </div>
    <div v-if="canDownloadMobileTickets || !event.date_confirmed" class="mt-2.5">
      <span class="font-semibold">{{ $t("Notice") }}</span>
      <ul class="mt-2.5 list-disc pl-5 text-sm">
        <li v-if="canDownloadMobileTickets">
          {{ $t("These tickets are designed for mobile") }}
        </li>
        <li v-if="!event.date_confirmed">
          {{
            $t(
              "The date/time has not yet been confirmed by the organiser of the event. Tickets will stay valid if the current date/time changes."
            )
          }}
        </li>
      </ul>
    </div>
  </div>
  <div v-else>{{ $t("Loading ticket info") }}...</div>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { formatDays } from "@/helpers/format.js";
import { useEventStore } from "@/store/event.js";

import {
  formatTime,
  formatDates,
  formatDate,
  deliveryDayOptions,
  SportType,
  TicketTypeOptions,
} from "@xs2event/js-lib";
import { useI18n } from "vue-i18n";
import { DateVisibility, type EventResponse } from "@xs2event/js-lib/api";
import { formatCurrency } from "@xs2event/js-lib";
import { camelToSnake } from "@/helpers/casing";
import type { PayDetailsResponse, PayDetailItemTicket } from "@xs2event/js-lib/api";
import { lookupCountryName } from "@/store/locations";

const { t, locale } = useI18n();
const props = defineProps<{
  ticket: unknown;
  ticket_id: string;
  event?: EventResponse;
  event_id?: string;
  bookingorder: unknown;
  category?: unknown;
  canDownloadMobileTickets?: boolean;
  paydetails?: PayDetailsResponse;
}>();

const eventStore = useEventStore();

const event = ref((props.event && camelToSnake(props.event)) || undefined);
const ticket = ref((props.ticket && camelToSnake(props.ticket)) || undefined);
const category = ref((props.category && camelToSnake(props.category)) || undefined);

const showDate = computed(() => event.value?.date_visibility !== DateVisibility.Hidden);
const showTime = computed(() => event.value?.date_visibility === DateVisibility.ShowDatetime);

const countryName = computed(() =>
  event.value?.iso_country ? lookupCountryName(event.value.iso_country, locale.value) : undefined
);

const paydetailsPerTicket = computed(
  () => props.paydetails?.tickets.find((ticket) => ticket.ticketId === props.ticket_id) || undefined
);

const fetchEvent = async (eventId) => {
  if (event.value) return;
  event.value = await eventStore.getEvent(eventId);
  eventStore.event = event.value;
};

const fetchTicket = async (ticketId) => {
  if (ticket.value) return;
  const value = await eventStore.getTicket(ticketId);
  ticket.value = value;
  fetchEvent(value.event_id);
  fetchCategory(value.category_id);
};
const fetchCategory = async (categoryId) => {
  if (category.value) return;
  category.value = await eventStore.getCategory(categoryId);
};

if (props.ticket_id) {
  fetchTicket(props.ticket_id);
}

if (props.event_id) {
  fetchEvent(props.event_id);
}

const deliveryDays = (days) => {
  const deliverydays = deliveryDayOptions.filter((e) => e.value === days)[0];
  if (deliverydays) {
    return t(deliverydays.text);
  }
};

const TicketType = (type) => {
  return TicketTypeOptions.find((e) => e.value === type).text;
};

const getEventDateText = (event): string | undefined => {
  if (!showDate.value || !event.date_start) {
    return undefined;
  }

  const date =
    event.sport_type === "formula1"
      ? formatDates(event.date_start, event.date_stop)
      : `${formatDate(event.date_start)} ${event.date_confirmed && showTime.value ? formatTime(event.date_start) : ""}`.trim();

  const status = event.date_confirmed ? t("Confirmed") : t("Not confirmed");
  return `${date} ${status}`;
};

const getTicketTypeText = (ticket, category): string | undefined => {
  const type = ticket?.type_ticket ? TicketType(ticket.type_ticket) : undefined;
  if (!type) {
    return undefined;
  }

  const homeSection = category?.sport_type === SportType.FOOTBALL ? `, ${t("home section")}` : "";
  return `${t(type)}${homeSection}`;
};

const getSeatingText = (category): string | undefined => {
  if (!category || !categoryHasSeats(category.category_type)) {
    return undefined;
  }

  if (!category.party_size_together) {
    return undefined;
  }

  return t("Up to {{num}} seats are guaranteed to be sitting together for this event", {
    num: category.party_size_together,
  });
};

const getDeliveryText = (category): string | undefined => {
  if (!category?.ticket_delivery_days) {
    return undefined;
  }

  return t("Your tickets will be delivered at least {{num}} ahead of the event", {
    num: deliveryDays(category.ticket_delivery_days),
  });
};

const getPaymentDetails = (item?: PayDetailItemTicket): string[] | undefined => {
  if (!item?.currency) {
    return undefined;
  }

  const details: string[] = [];
  if (item.subTotal != undefined) {
    details.push(formatCurrency(item.subTotal, item.currency));
  }
  if (item.netRate != undefined) {
    details.push(`${formatCurrency(item.netRate, item.currency)} ${t("each")}`);
  }
  return details.length ? details : undefined;
};

const getCategoryInformationText = (ticket) => {
  if (!ticket) {
    return "";
  }

  const ticketValidity = formatDays(
    ticket.ticket_validfrom || ticket.ticket_startdate,
    ticket.ticket_validuntil || ticket.ticket_stopdate
  );

  return `${ticket.category_name ?? ""}${ticketValidity ? ` (${ticketValidity})` : ""}`;
};

const getRenderEventInfoData = (event, ticket, category, paydetailsPerTicket?: PayDetailItemTicket) => {
  return {
    "Event name": event.event_name,
    Tournament: `${event.tournament_name} ${event.season ?? ""}`,
    Venue: event.venue_name,
    Date: getEventDateText(event),
    City: event.city,
    Country: countryName.value,
    "Ticket type": category ? getTicketTypeText(ticket, category) : undefined,
    Category: getCategoryInformationText(ticket),
    Seating: getSeatingText(category),
    Delivery: getDeliveryText(category),
    "Payment details": getPaymentDetails(paydetailsPerTicket),
  };
};

const eventInfo = computed(() => {
  if (!event.value || !ticket.value) return {};

  const data = getRenderEventInfoData(event.value, ticket.value, category.value, paydetailsPerTicket.value);

  return Object.fromEntries(Object.entries(data).filter(([, value]) => value != undefined && value !== ""));
});

const categoryHasSeats = (category_type) => {
  const category_with_seats = ["grandstand", "hospitality"];
  if (category_with_seats.includes(category_type)) {
    return true;
  }
};
</script>
